import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../lib/constants";
import { sendContactEmail } from "../lib/api";

const inputCls =
  "field-focus w-full bg-transparent border border-[#222] focus:border-[#D4AF37] text-white placeholder:text-[#555] rounded-xl px-4 py-3 outline-none transition-all duration-200 text-sm";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email) || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const emailRes = await sendContactEmail(form);
      if (emailRes.ok || emailRes.skipped) {
        toast.success("Message sent. Divy will reply shortly.");
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        toast.error("Couldn't send. Please try WhatsApp.");
        // eslint-disable-next-line no-console
        console.error("EmailJS contact error:", emailRes.error);
      }
    } catch {
      toast.error("Could not send. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
            Reach Out
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-light text-white">
            Let's build something <span className="gold-text italic">that converts.</span>
          </h2>
          <p className="mt-5 text-[#B0B0B0]">
            Prefer a quick chat? WhatsApp or email me directly. I personally read and
            reply to every message — usually within a few hours.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`tel:${SITE.phone.replace(/\s/g, "")}`}
              data-testid="contact-phone"
              className="group flex items-center gap-4 p-5 rounded-2xl bg-[#0A0A0A] border border-[#222] hover:border-[#D4AF37]/40 transition-colors"
            >
              <span className="w-11 h-11 rounded-xl glass-gold flex items-center justify-center">
                <Phone className="w-4.5 h-4.5 text-[#D4AF37]" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#737373]">
                  Phone
                </div>
                <div className="text-white">{SITE.phone}</div>
              </div>
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email"
              className="group flex items-center gap-4 p-5 rounded-2xl bg-[#0A0A0A] border border-[#222] hover:border-[#D4AF37]/40 transition-colors"
            >
              <span className="w-11 h-11 rounded-xl glass-gold flex items-center justify-center">
                <Mail className="w-4.5 h-4.5 text-[#D4AF37]" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#737373]">
                  Email
                </div>
                <div className="text-white">{SITE.email}</div>
              </div>
            </a>
            <a
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="group flex items-center gap-4 p-5 rounded-2xl bg-[#0A0A0A] border border-[#222] hover:border-[#25D366]/40 transition-colors"
            >
              <span className="w-11 h-11 rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 flex items-center justify-center">
                <MessageCircle className="w-4.5 h-4.5 text-[#25D366]" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#737373]">
                  WhatsApp
                </div>
                <div className="text-white">Chat instantly</div>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="contact-form"
          className="lg:col-span-7 glass rounded-3xl p-6 sm:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="contact-input-name"
              className={inputCls}
              placeholder="Your name *"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
            <input
              data-testid="contact-input-email"
              type="email"
              className={inputCls}
              placeholder="Email address *"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
          </div>
          <input
            data-testid="contact-input-phone"
            className={inputCls}
            placeholder="Phone number"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
          <textarea
            data-testid="contact-input-message"
            className={`${inputCls} min-h-[160px] resize-none`}
            placeholder="Tell me a bit about your project *"
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="btn-gold-glow inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] disabled:opacity-60 text-black font-semibold rounded-full px-6 py-3 transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                Send Message <Send className="w-4 h-4" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
